/**
 * Signing in with the code from the email, separated from the pages so it can
 * be tested (#54, following the shape #320 gave `auth-callback-lib.ts`).
 *
 * This returns a decision instead of performing it: navigation, DOM and logging
 * stay in `login.ts` / `register.ts`; everything worth asserting is here.
 */

/** `mailer_otp_length` in the production Auth config. */
export const OTP_CODE_LENGTH = 6;

/** `smtp_max_frequency` in the production Auth config, in seconds. */
export const RESEND_COOLDOWN_SECONDS = 60;

export interface OtpSession {
  access_token: string;
  user: { user_metadata: { onboarded?: unknown } };
}

export type OtpCodeResult =
  | { ok: true; target: "/app/" | "/app/?welcome=1" }
  | {
      ok: false;
      // `empty`:  not a full-length code; nothing was sent.
      // `verify`: GoTrue refused. Wrong, expired, spent and never-existed all
      //   arrive here as one 403/otp_expired, so the pages cannot -- and must
      //   not -- tell them apart.
      // `threw`:  anything unexpected, kept off the happy path.
      reason: "empty" | "verify" | "threw";
      detail: string | null;
    };

export interface OtpCodeDeps {
  email: string;
  token: string;
  // `type: "email"` is not a choice the caller makes: it is the one value that
  // verifies both a signup token and a magic-link token, which matters because
  // the login form mails `confirmation.html` to an unconfirmed account.
  verifyOtp: (args: {
    email: string;
    token: string;
    type: "email";
  }) => Promise<{ session: OtpSession | null; error: unknown }>;
  completeRegistration: (accessToken: string) => Promise<void>;
}

function messageOf(error: unknown): string | null {
  if (error === null || error === undefined) return null;
  if (error instanceof Error) return error.message;
  return typeof error === "string" ? error : JSON.stringify(error);
}

export async function runOtpCodeSignIn(
  deps: OtpCodeDeps,
): Promise<OtpCodeResult> {
  const { email, verifyOtp, completeRegistration } = deps;
  const token = deps.token.trim();
  if (token.length !== OTP_CODE_LENGTH) {
    return { ok: false, reason: "empty", detail: null };
  }

  try {
    const { session, error } = await verifyOtp({ email, token, type: "email" });
    if (error !== null || session === null) {
      return { ok: false, reason: "verify", detail: messageOf(error) };
    }

    const onboarded = session.user.user_metadata.onboarded === true;
    if (!onboarded) {
      // Best-effort, and deliberately awaited: /app/ renders the persisted name
      // on boot, so this has to finish first. A failure here must not strand a
      // user who is already signed in.
      try {
        await completeRegistration(session.access_token);
      } catch (err) {
        console.error("complete-registration failed", err);
      }
    }
    return { ok: true, target: onboarded ? "/app/" : "/app/?welcome=1" };
  } catch (err) {
    return { ok: false, reason: "threw", detail: messageOf(err) };
  }
}

/**
 * Seconds left before another mail may be requested.
 *
 * Counted from the previous send rather than from the click, because GoTrue's
 * clock starts at the mail: a timer started on click would show "ready" while
 * the server still answers 429.
 */
export function resendSecondsLeft(lastSentAt: number, now: number): number {
  const elapsed = Math.floor((now - lastSentAt) / 1000);
  return Math.max(0, RESEND_COOLDOWN_SECONDS - elapsed);
}

export function formatCountdown(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  return `${String(minutes)}:${String(seconds % 60).padStart(2, "0")}`;
}
